'use client'

import React, {
  createContext,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useState,
  JSX,
} from 'react'

import { useAddressSearchHistory } from '~/hooks/useAddressSearchHistory'
import { useOnboarding } from '~/hooks/useOnboarding'
import { useOrgContacts } from '~/hooks/useOrgContacts'
import { useCookieStore } from '~/store/cookieStore'
import { setTrackingUserProperties } from '~/utils/tracking'

type LoginModalState = boolean
export type GlobalModalState = {
  isOpen: boolean
  content: ReactNode
  onDismiss?: () => void
}

type IAuthContext = {
  loginModalState: LoginModalState
  setLoginModalState: (state: LoginModalState) => void
  globalModalState: GlobalModalState
  setGlobalModalState: (state: GlobalModalState) => void
  closeGlobalModal: () => void
  onboarding: ReturnType<typeof useOnboarding>
  addressSearchHistory: ReturnType<typeof useAddressSearchHistory>
}

const AuthContext = createContext<IAuthContext | undefined>(undefined)

export function AuthProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const authToken = useCookieStore((s) => s.authToken)

  // Load the contacts before any consumer mounts.
  useOrgContacts()

  const [loginModalState, setLoginModalState] = useState<LoginModalState>(false)
  const [globalModalState, setGlobalModalState] = useState<GlobalModalState>({
    isOpen: false,
    content: null,
  })

  const closeGlobalModal = useCallback(() => {
    setGlobalModalState({ isOpen: false, content: null })
  }, [setGlobalModalState])

  // Reset analytics identity to anonymous for logged-out users (the authed
  // identity is set in useAuthUser once the user loads). No query runs without a
  // token, so this side effect can't hang off useQuery.
  // eslint-disable-next-line local/noUseEffect
  useEffect(() => {
    if (!authToken) {
      setTrackingUserProperties({
        userId: 'undefined',
        isTestUser: false,
      })
    }
  }, [authToken])

  const onboarding = useOnboarding()
  const addressSearchHistory = useAddressSearchHistory()

  const authContextValue = React.useMemo(
    () => ({
      addressSearchHistory,
      onboarding,
      loginModalState,
      setLoginModalState,
      globalModalState,
      setGlobalModalState,
      closeGlobalModal,
    }),
    [
      addressSearchHistory,
      onboarding,
      loginModalState,
      setLoginModalState,
      globalModalState,
      setGlobalModalState,
      closeGlobalModal,
    ],
  )

  return <AuthContext value={authContextValue}>{children}</AuthContext>
}

export const useAuth: () => IAuthContext = () => {
  const context = use(AuthContext)
  if (!context) throw new Error('useAuth must be within AuthProvider')

  return context
}
